import { paramCase as kebabCase } from 'change-case';
import { countBy, omit } from 'lodash';
import React from 'react';
import { parseLayerId } from '@/framework';
import { registerStyleSheet } from '@/utils/common-kit';
import { Cell, CellStyle, ITableLayerProps } from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import {
    DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
    DEFAULT_TABLE_CELL__LINE_HEIGHT,
    DEFAULT_TABLE_CELL__TEXT_ALIGN,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '../default-configs';
import { getCellStyle } from '../utils/table';
import { TableLoadingParams } from './table-layer';

export const getTdElementId = (cell: { id: string; hIndex: number; vIndex: number }) =>
    `td-${cell.hIndex}-${cell.vIndex}--${cell.id}`;

export const getCellInfoListByMousePoint = (point: { clientX: number; clientY: number }, layerId: string) => {
    // document.elementsFromPoint需要可点击的dom才能获取，所以这里临时开启可点击
    const removeStyleSheet = registerStyleSheet(`
        [data-layer-id^="${parseLayerId(layerId).id}"] td {
            pointer-events: initial;
        }
    `);
    const targetCellInfoList = document
        .elementsFromPoint(point.clientX, point.clientY)
        .map((el) => {
            if (!(el instanceof HTMLTableCellElement)) return null;
            const [_, hIndex, vIndex] = el.id.match(/^td-(\d*?)-(\d*?)--.*/)!;
            return {
                id: el.dataset.tableCellId!,
                hIndex: +hIndex,
                vIndex: +vIndex,
            };
        })
        .filter(Boolean) as { id: string; hIndex: number; vIndex: number }[];
    removeStyleSheet();
    return targetCellInfoList;
};

const getCellTextWrapStyle = (style: CellInnerComponentProps['style']): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
        whiteSpace: 'break-spaces',
        textDecoration: 'inherit',
    };
    const restStyle: React.CSSProperties = style.disableAutoWrap
        ? {
              paddingLeft: TABLE_CELL_HORIZONTAL_PADDING,
              paddingRight: TABLE_CELL_HORIZONTAL_PADDING,
              position: 'absolute',
              top: 0,
              left: 0,
              display: 'flex',
              width: 'max-content',
              minWidth: '100%',
              height: '100%',
              wordBreak: 'break-all',
              lineBreak: 'anywhere',
              outline: 'none',
              justifyContent: {
                  left: 'flex-start',
                  center: 'center',
                  right: 'flex-end',
              }[style.textAlign ?? DEFAULT_TABLE_CELL__TEXT_ALIGN],
              alignItems: {
                  top: 'start',
                  center: 'center',
                  bottom: 'end',
              }[style.verticalAlign ?? DEFAULT_TABLE_CELL__VERTICAL_ALIGN],
          }
        : {};
    return Object.assign(baseStyle, restStyle);
};

interface CellInnerComponentProps {
    style: CellStyle;
    cell: Cell;
}

const CellInnerComponent: React.FC<CellInnerComponentProps> = (props) => {
    const text = props.cell.content;
    const enterCount = countBy(text)['\n'] ?? 0;

    return (
        <>
            {/* 以此撑开单元格高度 */}
            {props.style.disableAutoWrap && text.length > 0 && (
                <div className="cell-text-block">
                    {Array(enterCount)
                        .fill(null)
                        .map((_, index) => (
                            <br key={`br-${index}`} />
                        ))}
                    {/* 第一个br不占高度, 所以需要补一个 */}
                    <br />
                </div>
            )}

            <div style={getCellTextWrapStyle(props.style)}>
                <span className="cell-text">
                    {text}
                    {/* 最后一个字符如果是\n则不会占高度, 所以需要补一个br */}
                    <br />
                </span>
            </div>
        </>
    );
};

const getCellInnerComponentHTML = (props: CellInnerComponentProps): string => {
    const text = props.cell.content;
    const enterCount = countBy(text)['\n'] ?? 0;

    const Block =
        props.style.disableAutoWrap && text.length > 0
            ? `<div class="cell-text-block">${Array(enterCount).fill('<br />').join('')}<br /></div>`
            : '';

    return `
        ${Block}
        <div
            style="${parseReactStyle2CSSText(getCellTextWrapStyle(props.style))}"
        ><span class="cell-text">${text}<br /></span></div>
    `;
};

const getCellStyleInfo = (
    { cell, tableLayerData: { allStyleId, styles, presetId }, fontFamilyMap }: CellComponentProps,
    overwriteStyle?: CellStyle,
): {
    cellStyle: CellStyle;
    cssStyle: React.CSSProperties;
} => {
    const originalCellStyle = getCellStyle(cell, { allStyleId, styles, presetId });
    const cellStyle = {
        ...originalCellStyle,
        ...overwriteStyle,
    };
    const border = {
        top: cellStyle.border?.top?.width ?? 0,
        left: cellStyle.border?.left?.width ?? 0,
        right: cellStyle.border?.right?.width ?? 0,
        bottom: cellStyle.border?.bottom?.width ?? 0,
    };
    const borderCssStyle: React.CSSProperties = {
        // 向下取整, 防止精度问题导致表格边框有缝隙 (背景色直接透上来), 只要是画布缩放, 最终展示都不会是整数像素值, 只能尽量避免缝隙问题
        borderLeft: `${Math.floor(border.left)}px solid transparent`,
        borderTop: `${Math.floor(border.top)}px solid transparent`,
        borderRight: `${Math.floor(border.right)}px solid transparent`,
        borderBottom: `${Math.floor(border.bottom)}px solid transparent`,
    };
    const getFontFamilyStyleValue = () => {
        const realFontFamily = cellStyle.fontFamily ?? DEFAULT_FONT_FAMILY().name;
        const subFontFamily = fontFamilyMap[realFontFamily];
        return subFontFamily ? `${realFontFamily}, ${subFontFamily}` : realFontFamily;
    };
    const cssStyle: React.CSSProperties = {
        position: 'relative',
        paddingLeft: TABLE_CELL_HORIZONTAL_PADDING,
        paddingRight: TABLE_CELL_HORIZONTAL_PADDING,
        /** 不加height的话, 底部对齐模式下文字的位置会不对 */
        height: 'var(--cell-height, 100%)',
        backgroundColor: cellStyle.backgroundColor ?? DEFAULT_TABLE_CELL__BACKGROUND_COLOR,
        boxSizing: 'border-box',
        overflow: 'hidden',
        userSelect: 'none',
        lineHeight: DEFAULT_TABLE_CELL__LINE_HEIGHT,
        color: cellStyle.color ?? DEFAULT_TABLE_CELL__TEXT_COLOR,
        fontFamily: getFontFamilyStyleValue(),
        fontSize: cellStyle.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
        textAlign: {
            left: 'left',
            center: 'center',
            right: 'right',
        }[cellStyle.textAlign ?? DEFAULT_TABLE_CELL__TEXT_ALIGN],
        verticalAlign: {
            top: 'top',
            center: 'middle',
            bottom: 'bottom',
        }[cellStyle.verticalAlign ?? DEFAULT_TABLE_CELL__VERTICAL_ALIGN],
        fontWeight: cellStyle.bold ? 'bold' : 'normal',
        fontStyle: cellStyle.italic ? 'italic' : 'normal',
        textDecoration: `${cellStyle.lineThrough ? 'line-through' : ''} ${cellStyle.underline ? 'underline' : ''}`,
        outline: 'none',
        wordBreak: 'break-all',
        lineBreak: 'anywhere',
        textWrap: cellStyle.disableAutoWrap ? 'nowrap' : 'wrap',
        ...borderCssStyle,
    };
    return {
        cssStyle,
        cellStyle,
    };
};

interface CellComponentProps {
    cell: Cell;
    tableLayerData: Pick<ITableLayerProps, 'allStyleId' | 'styles' | 'presetId'>;
    fontFamilyMap: Record<string, string>;
    isInEditorLayer?: boolean;
    cellIdsOfLoadingFont?: string[];
    tableLoadingParams?: TableLoadingParams;
}

const CellComponent: React.FC<CellComponentProps> = (props) => {
    const { cell, isInEditorLayer, tableLoadingParams } = props;
    const { cssStyle, cellStyle } = getCellStyleInfo(props);
    const onlyShowLoading = tableLoadingParams?.onlyShowLoading ?? false;
    const loadingNode = tableLoadingParams?.loadingNode?.(cell.__renderId);
    return (
        <td
            id={getTdElementId({ id: cell.__renderId, hIndex: cell.hIndex, vIndex: cell.vIndex })}
            className="tableCell"
            // 用于编辑态的dom获取
            data-table-cell-id={isInEditorLayer ? cell.__renderId : ''}
            rowSpan={cell.rowSpan}
            colSpan={cell.colSpan}
            style={onlyShowLoading ? omit(cssStyle, ['backgroundColor', 'color', 'textDecoration']) : cssStyle}
        >
            {loadingNode}
            {!onlyShowLoading && !!cell.content && <CellInnerComponent cell={cell} style={cellStyle} />}
        </td>
    );
};

export const getCellComponentHTML = ({
    cellComponentProps: props,
    overwriteStyle,
    newContent,
}: {
    cellComponentProps: CellComponentProps;
    overwriteStyle?: Partial<CellStyle>;
    newContent?: string;
}): string => {
    const { cell: originalCell, isInEditorLayer } = props;
    const cell: Cell = {
        ...originalCell,
        content: newContent ?? originalCell.content,
    };
    const { cssStyle, cellStyle } = getCellStyleInfo(props, overwriteStyle);
    const cellInnerComponentHTML = cell.content
        ? getCellInnerComponentHTML({
              cell,
              style: cellStyle,
          })
        : '';
    return `
        <td
            id="${getTdElementId({ id: cell.__renderId, hIndex: cell.hIndex, vIndex: cell.vIndex })}"
            class="tableCell"
            data-table-cell-id="${isInEditorLayer ? cell.__renderId : ''}"
            rowSpan="${cell.rowSpan}"
            colSpan="${cell.colSpan}"
            style="${parseReactStyle2CSSText(cssStyle)}"
        >${cellInnerComponentHTML}</td>
    `;
};

const parseReactStyle2CSSText = (reactStyle: React.CSSProperties): string => {
    let cssText = '';
    Object.entries(reactStyle).forEach(([key, value]) => {
        if (value == null) return;
        const realKey = kebabCase(key);
        const realValue =
            [
                'width',
                'height',
                'maxWidth',
                'maxHeight',
                'minWidth',
                'minHeight',
                'margin',
                'marginTop',
                'marginLeft',
                'marginRight',
                'marginBottom',
                'padding',
                'paddingTop',
                'paddingLeft',
                'paddingRight',
                'paddingBottom',
                'fontSize',
                'textIndent',
                'border',
                'borderWidth',
            ].includes(key) && typeof value === 'number'
                ? value + 'px'
                : value;
        cssText += `${realKey}: ${realValue};`;
    });
    return cssText;
};

export default CellComponent;
